import { useCallback } from 'react';
import { useLanguage } from '../components/LanguageProvider';
import { signIn, signUp, type SignInInput, type SignUpInput, type SignUpOutput } from 'aws-amplify/auth';
import { I18n } from 'aws-amplify/utils';
import { isSameDomain } from '../utils/auth';

// Amplify UIのhub eventsを利用して、エラーを通知する
import { Hub } from 'aws-amplify/utils';

export const useAuthServices = () => {
    const { language } = useLanguage();

    const handlePostAuthSuccess = useCallback(async () => {
        try {
            // Amplifyが自動的にクッキーを管理するため、手動操作は不要
            // リダイレクト処理のみ実行
            const urlParams = new URLSearchParams(window.location.search);
            const redirectUri = urlParams.get('redirect_uri');
            if (redirectUri && isSameDomain(redirectUri)) {
                window.location.href = redirectUri;
            }
        } catch (error) {
            console.error('Error during post-auth handling:', error);
        }
    }, []);

    const handleSignIn = useCallback(async (formData: SignInInput) => {
        const { username, password } = formData;
        if (!username || !password) {
            console.error('Username and password are required');
            // エラーをHubに発行して、UIに通知する
            Hub.dispatch('auth', {
                event: 'signIn_failure',
                data: new Error('Username and password are required')
            });

            // 代わりにサインイン失敗を示す結果オブジェクトを返す
            return {
                isSignedIn: false,
                nextStep: {
                    signInStep: 'DONE',
                }
            };
        }

        try {
            const result = await signIn({
                username,
                password,
                options: {
                    authFlowType: 'USER_PASSWORD_AUTH'
                }
            });

            if (result.isSignedIn) {
                await handlePostAuthSuccess();
            }
            return result;
        } catch (error) {
            console.error('Error during sign in:', error);
            // 例外をcatchする代わりに、エラー情報をHubに発行
            Hub.dispatch('auth', {
                event: 'signIn_failure',
                data: error
            });

            // 代わりにサインイン失敗を示す結果オブジェクトを返す
            return {
                isSignedIn: false,
                nextStep: {
                    signInStep: 'DONE',
                }
            };
        }
    }, [handlePostAuthSuccess]);

    const handleSignUp = useCallback(async (formData: SignUpInput): Promise<SignUpOutput> => {
        try {
            const { username, password, options } = formData;

            const userAttributes = {
                ...(options?.userAttributes || {}),
                locale: language
            };

            const signUpResult = await signUp({
                username,
                password,
                options: {
                    ...options,
                    userAttributes,
                },
            });

            if (signUpResult.isSignUpComplete) {
                // サインアップ後の自動サインイン
                try {
                    await handleSignIn({ username, password });
                } catch (signInError) {
                    console.error('Auto sign-in failed after sign-up:', signInError);
                }
            }

            return signUpResult;
        } catch (error) {
            console.error('Error during sign up:', error);
            Hub.dispatch('auth', {
                event: 'signUp_failure',
                data: error
            });

            throw error;
        }
    }, [language, handleSignIn]);

    const validateCustomSignUp = useCallback(async (formData: { acknowledgement: any }) => {
        if (!formData.acknowledgement) {
            return {
                acknowledgement: I18n.get('termsAndConditionsError'),
            };
        }
        return undefined;
    }, []);

    return {
        handleSignUp,
        handleSignIn,
        validateCustomSignUp,
    };
};
